import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { SESSION_ACTIVITY_EVENT, SESSION_EXPIRED_EVENT } from "./sessionEvents";

const SessionExpiryHandler = () => {
  const { authenticated, expireSession, user } = useAuth();
  const inactivityTimeoutMs = Number(user?.sessionTimeoutSeconds) * 1000;
  const location = useLocation();
  const navigate = useNavigate();
  const locationRef = useRef(location);
  locationRef.current = location;

  useEffect(() => {
    if (
      !authenticated ||
      !Number.isFinite(inactivityTimeoutMs) ||
      inactivityTimeoutMs <= 0
    )
      return undefined;

    let lastActivity = Date.now();
    let timeoutId;
    let hasExpired = false;
    const activityChannel =
      typeof BroadcastChannel === "undefined"
        ? null
        : new BroadcastChannel("jstarter-session-activity");

    const goToLogin = () => {
      if (hasExpired) return;
      hasExpired = true;
      const currentLocation = locationRef.current;
      expireSession();
      navigate("/login", {
        replace: true,
        state: {
          sessionExpired: true,
          from: {
            pathname: currentLocation.pathname,
            search: currentLocation.search,
            hash: currentLocation.hash,
          },
        },
      });
    };

    const scheduleExpiry = () => {
      window.clearTimeout(timeoutId);
      const remaining = inactivityTimeoutMs - (Date.now() - lastActivity);
      if (remaining <= 0) {
        goToLogin();
      } else {
        timeoutId = window.setTimeout(goToLogin, remaining);
      }
    };

    const recordActivity = () => {
      lastActivity = Date.now();
      scheduleExpiry();
      activityChannel?.postMessage("activity");
    };

    const recordCrossTabActivity = () => {
      lastActivity = Date.now();
      scheduleExpiry();
    };

    const checkExpiryOnReturn = () => {
      if (document.visibilityState === "visible") scheduleExpiry();
    };

    window.addEventListener(SESSION_ACTIVITY_EVENT, recordActivity);
    window.addEventListener(SESSION_EXPIRED_EVENT, goToLogin);
    activityChannel?.addEventListener("message", recordCrossTabActivity);
    document.addEventListener("visibilitychange", checkExpiryOnReturn);
    scheduleExpiry();

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener(SESSION_ACTIVITY_EVENT, recordActivity);
      window.removeEventListener(SESSION_EXPIRED_EVENT, goToLogin);
      activityChannel?.close();
      document.removeEventListener("visibilitychange", checkExpiryOnReturn);
    };
  }, [authenticated, expireSession, inactivityTimeoutMs, navigate]);

  return null;
};

export default SessionExpiryHandler;
